import { useTranslation } from "react-i18next";

import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { ClientCatalogItem } from "@/services/clientCatalogService";

const NONE = "__none__";

/**
 * An entry in the picker, from either catalog.
 *
 * `origin` exists because the list is merged from two sources with different
 * meanings: a client item is a price agreed with THIS client, a product is the
 * company's standard price. When both are offered the accountant has to be
 * able to tell which one they are picking — otherwise the merge quietly
 * invites the wrong price onto an invoice.
 */
export type CatalogPickerItem = ClientCatalogItem & {
  origin?: "client" | "product";
};

export interface InvoiceLineCatalogPickerProps {
  items: CatalogPickerItem[];
  disabled?: boolean;
  selectedCatalogItemId?: string | null;
  onSelectItem: (item: CatalogPickerItem) => void;
  onClearSelection: () => void;
}

export function InvoiceLineCatalogPicker({
  items,
  disabled,
  selectedCatalogItemId,
  onSelectItem,
  onClearSelection,
}: InvoiceLineCatalogPickerProps): JSX.Element | null {
  const { t } = useTranslation();

  if (items.length === 0) {
    return null;
  }

  const hasBothOrigins =
    items.some((i) => i.origin === "client") &&
    items.some((i) => i.origin === "product");

  return (
    <div className="w-full min-w-[min(100%,14rem)] max-w-md space-y-1">
      <Label className="text-xs whitespace-nowrap text-muted-foreground">
        {items.some((i) => i.origin === "client")
          ? t("invoices.new.catalogLineLabel", "From client list")
          : t("invoices.new.catalogLineLabelProducts", "Productos")}
      </Label>
      <Select
        value={selectedCatalogItemId ?? NONE}
        onValueChange={(v) => {
          if (v === NONE) {
            onClearSelection();
            return;
          }
          const item = items.find((i) => i.id === v);
          if (item) onSelectItem(item);
        }}
        disabled={disabled}
      >
        <SelectTrigger className="h-9 transition-colors duration-150">
          <SelectValue
            placeholder={t(
              "invoices.new.catalogLinePlaceholder",
              "Choose or type below"
            )}
          />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>
            {t("invoices.new.catalogLineManual", "Type manually below")}
          </SelectItem>
          {items.map((item) => (
            <SelectItem key={item.id} value={item.id}>
              {item.sku?.trim()
                ? `${item.sku.trim()} — ${item.description}`
                : item.description}
              {/* Only worth labelling when both sources are present; a lone
                  source makes the suffix noise on every row. */}
              {item.origin === "client" && hasBothOrigins ? (
                <span className="ml-1 text-xs text-muted-foreground">
                  {t("invoices.new.catalogOriginClient", "(precio del cliente)")}
                </span>
              ) : null}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
